import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Doux ce Moman';
const SITE_URL = 'https://douxcemoman.fr';
const DEFAULT_IMAGE = `${SITE_URL}/accueil_image.png`;
const DEFAULT_DESCRIPTION = 'Centre de bien-être périnatal près de Besançon, à Geneuille (25). Massage prénatal, spa bébé, bain enveloppé, ateliers parent-enfant. Réservation en ligne.';

/**
 * Sérialise un schéma JSON-LD pour insertion dans une balise <script>.
 *
 * react-helmet-async écrit le contenu des balises script tel quel, sans
 * échappement. Un texte issu du back office (réponse de FAQ, article de blog)
 * contenant « </script> » refermerait donc le bloc et permettrait d'injecter du
 * HTML dans la page publique. On échappe « < » en < : cela reste du JSON
 * valide, correctement interprété par les moteurs de recherche.
 */
const serializeJsonLd = (schema: unknown): string =>
  JSON.stringify(schema).replace(/</g, '\\u003c');

const PAGE_LABELS: Record<string, string> = {
  '/services': 'Services',
  '/a-propos': 'À propos',
  '/reservation': 'Réservation',
  '/bons-cadeaux': 'Bons cadeaux',
  '/forfaits': 'Forfaits',
  '/blog': "Mag'Moman",
  '/faq': 'FAQ',
  '/contact': 'Contact',
  '/mentions-legales': 'Mentions légales',
  '/cgv': 'CGV',
  '/privacy-policy': 'Politique de confidentialité',
};

function buildBreadcrumbJsonLd(path: string, title: string) {
  if (!path || path === '/') return null;
  const items = [
    { '@type': 'ListItem', position: 1, name: 'Accueil', item: SITE_URL },
    { '@type': 'ListItem', position: 2, name: PAGE_LABELS[path] || title, item: `${SITE_URL}${path}` },
  ];
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

interface SEOHeadProps {
  title: string;
  description?: string;
  path?: string;
  image?: string;
  type?: 'website' | 'article';
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd,
}) => {
  const fullTitle = path === '' || path === '/'
    ? `${SITE_NAME} | Bien-être pour futures mamans et bébés`
    : `${title} | ${SITE_NAME}`;
  const canonicalUrl = `${SITE_URL}${path}`;
  const fullImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const breadcrumb = buildBreadcrumbJsonLd(path, title);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />

      {/* BreadcrumbList */}
      {breadcrumb && (
        <script type="application/ld+json">
          {serializeJsonLd(breadcrumb)}
        </script>
      )}

      {/* Page-specific Structured Data */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((schema, i) => (
        <script key={i} type="application/ld+json">
          {serializeJsonLd(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHead;
